import api from './api';
import type {
  Reservation,
  ReservationRequest,
  TableGroup,
  TableGroupRequest,
  WaitlistEntry,
  WaitlistRequest,
  TableCompatibility,
  TableCompatibilityRequest,
  TimeSlot,
  TableSuggestion,
  AvailabilityCheckResult,
  ReservableTable,
  ReservationSettings,
  ReservationDailyStats,
  WaitlistStats,
} from '../types/reservation.types';

// ============================================================================
// RESERVATIONS
// ============================================================================

export const reservationApi = {
  // Create a new reservation
  create: async (data: ReservationRequest): Promise<Reservation> => {
    const response = await api.post<Reservation>('/api/reservations', data);
    return response.data;
  },

  // Get reservation by ID
  getById: async (id: number): Promise<Reservation> => {
    const response = await api.get<Reservation>(`/api/reservations/${id}`);
    return response.data;
  },

  // Get reservation by code
  getByCode: async (code: string): Promise<Reservation> => {
    const response = await api.get<Reservation>(`/api/reservations/code/${code}`);
    return response.data;
  },

  // Get reservations by date
  getByDate: async (date: string): Promise<Reservation[]> => {
    const response = await api.get<Reservation[]>(`/api/reservations/date/${date}`);
    return response.data;
  },

  // Get weekly reservations (for calendar)
  getWeekly: async (startDate: string, endDate: string): Promise<Reservation[]> => {
    const response = await api.get<Reservation[]>('/api/reservations/calendar/weekly', {
      params: { startDate, endDate },
    });
    return response.data;
  },

  // Get daily stats
  getDailyStats: async (date?: string): Promise<ReservationDailyStats> => {
    const response = await api.get<ReservationDailyStats>('/api/reservations/calendar/daily', {
      params: date ? { date } : {},
    });
    return response.data;
  },

  // Get active reservations
  getActive: async (): Promise<Reservation[]> => {
    const response = await api.get<Reservation[]>('/api/reservations/active');
    return response.data;
  },

  // Update reservation
  update: async (id: number, data: Partial<ReservationRequest>): Promise<Reservation> => {
    const response = await api.put<Reservation>(`/api/reservations/${id}`, data);
    return response.data;
  },

  // Change status
  changeStatus: async (
    id: number,
    status: string,
    reason?: string
  ): Promise<Reservation> => {
    const response = await api.patch<Reservation>(`/api/reservations/${id}/status`, {
      status,
      reason,
    });
    return response.data;
  },

  // Confirm reservation
  confirm: async (id: number): Promise<Reservation> => {
    return reservationApi.changeStatus(id, 'CONFIRMED');
  },

  // Seat reservation
  seat: async (id: number): Promise<Reservation> => {
    return reservationApi.changeStatus(id, 'SEATED');
  },

  // Complete reservation
  complete: async (id: number): Promise<Reservation> => {
    return reservationApi.changeStatus(id, 'COMPLETED');
  },

  // Cancel reservation
  cancel: async (id: number, reason?: string): Promise<Reservation> => {
    const response = await api.delete<Reservation>(`/api/reservations/${id}`, {
      params: reason ? { reason } : {},
    });
    return response.data;
  },

  // Mark as no-show
  markNoShow: async (id: number): Promise<Reservation> => {
    return reservationApi.changeStatus(id, 'NO_SHOW');
  },
};

// ============================================================================
// TABLE GROUPS
// ============================================================================

export const tableGroupApi = {
  // Create a new table group (merge tables)
  create: async (data: TableGroupRequest): Promise<TableGroup> => {
    const response = await api.post<TableGroup>('/api/table-groups', data);
    return response.data;
  },

  // Get active groups
  getActive: async (): Promise<TableGroup[]> => {
    const response = await api.get<TableGroup[]>('/api/table-groups');
    return response.data;
  },

  // Get group by ID
  getById: async (id: number): Promise<TableGroup> => {
    const response = await api.get<TableGroup>(`/api/table-groups/${id}`);
    return response.data;
  },

  // Get group by code
  getByCode: async (code: string): Promise<TableGroup> => {
    const response = await api.get<TableGroup>(`/api/table-groups/code/${code}`);
    return response.data;
  },

  // Add table to group
  addTable: async (groupId: number, tableId: number): Promise<TableGroup> => {
    const response = await api.post<TableGroup>(
      `/api/table-groups/${groupId}/tables/${tableId}`
    );
    return response.data;
  },

  // Remove table from group
  removeTable: async (groupId: number, tableId: number): Promise<TableGroup> => {
    const response = await api.delete<TableGroup>(
      `/api/table-groups/${groupId}/tables/${tableId}`
    );
    return response.data;
  },

  // Link billing (order line)
  linkBilling: async (groupId: number, orderLineId: number): Promise<TableGroup> => {
    const response = await api.post<TableGroup>(
      `/api/table-groups/${groupId}/link-billing/${orderLineId}`
    );
    return response.data;
  },

  // Close group (unmerge)
  close: async (groupId: number): Promise<TableGroup> => {
    const response = await api.post<TableGroup>(`/api/table-groups/${groupId}/close`);
    return response.data;
  },

  // Check if table is in a group
  checkTable: async (
    tableId: number
  ): Promise<{ inGroup: boolean; group: TableGroup | null }> => {
    const response = await api.get<{ inGroup: boolean; group: TableGroup | null }>(
      `/api/table-groups/check-table/${tableId}`
    );
    return response.data;
  },
};

// ============================================================================
// WAITLIST
// ============================================================================

export const waitlistApi = {
  // Add to waitlist
  add: async (data: WaitlistRequest): Promise<WaitlistEntry> => {
    const response = await api.post<WaitlistEntry>('/api/waitlist', data);
    return response.data;
  },

  // Get current waitlist
  getAll: async (): Promise<WaitlistEntry[]> => {
    const response = await api.get<WaitlistEntry[]>('/api/waitlist');
    return response.data;
  },

  // Get by id
  getById: async (id: number): Promise<WaitlistEntry> => {
    const response = await api.get<WaitlistEntry>(`/api/waitlist/${id}`);
    return response.data;
  },

  // Get by code (for customer check)
  getByCode: async (code: string): Promise<WaitlistEntry> => {
    const response = await api.get<WaitlistEntry>(`/api/waitlist/code/${code}`);
    return response.data;
  },

  // Notify customer
  notify: async (
    id: number,
    tableId?: number,
    tableGroupId?: number
  ): Promise<WaitlistEntry> => {
    const response = await api.post<WaitlistEntry>(`/api/waitlist/${id}/notify`, {
      tableId,
      tableGroupId,
    });
    return response.data;
  },

  // Seat customer
  seat: async (id: number): Promise<WaitlistEntry> => {
    const response = await api.post<WaitlistEntry>(`/api/waitlist/${id}/seat`);
    return response.data;
  },

  // Remove from waitlist
  remove: async (id: number, reason?: string): Promise<WaitlistEntry> => {
    const response = await api.delete<WaitlistEntry>(`/api/waitlist/${id}`, {
      params: reason ? { reason } : {},
    });
    return response.data;
  },

  // Get statistics
  getStats: async (): Promise<WaitlistStats> => {
    const response = await api.get<WaitlistStats>('/api/waitlist/stats');
    return response.data;
  },

  // Get wait estimate
  getEstimate: async (partySize: number): Promise<{ estimatedMinutes: number }> => {
    const response = await api.get<{ estimatedMinutes: number }>('/api/waitlist/estimate', {
      params: { partySize },
    });
    return response.data;
  },
};

// ============================================================================
// TABLE COMPATIBILITY
// ============================================================================

export const tableCompatibilityApi = {
  // Create compatibility rule
  create: async (data: TableCompatibilityRequest): Promise<TableCompatibility> => {
    const response = await api.post<TableCompatibility>('/api/table-compatibility', data);
    return response.data;
  },

  // Get all rules
  getAll: async (activeOnly = true): Promise<TableCompatibility[]> => {
    const response = await api.get<TableCompatibility[]>('/api/table-compatibility', {
      params: { activeOnly },
    });
    return response.data;
  },

  // Get compatible tables for a table
  getCompatibleTables: async (tableId: number): Promise<ReservableTable[]> => {
    const response = await api.get<ReservableTable[]>(
      `/api/table-compatibility/table/${tableId}`
    );
    return response.data;
  },

  // Suggest combinations for party size
  suggest: async (partySize: number): Promise<TableCompatibility[]> => {
    const response = await api.get<TableCompatibility[]>('/api/table-compatibility/suggest', {
      params: { partySize },
    });
    return response.data;
  },

  // Update rule
  update: async (
    id: number,
    data: Partial<TableCompatibilityRequest>
  ): Promise<TableCompatibility> => {
    const response = await api.put<TableCompatibility>(
      `/api/table-compatibility/${id}`,
      data
    );
    return response.data;
  },

  // Delete rule
  delete: async (id: number): Promise<void> => {
    await api.delete(`/api/table-compatibility/${id}`);
  },

  // Check if two tables are compatible
  check: async (
    table1Id: number,
    table2Id: number
  ): Promise<{ table1Id: number; table2Id: number; compatible: boolean }> => {
    const response = await api.get<{
      table1Id: number;
      table2Id: number;
      compatible: boolean;
    }>('/api/table-compatibility/check', {
      params: { table1Id, table2Id },
    });
    return response.data;
  },
};

// ============================================================================
// AVAILABILITY
// ============================================================================

export const availabilityApi = {
  // Check availability
  check: async (
    date: string,
    startTime: string,
    endTime: string,
    partySize: number,
    tableId?: number
  ): Promise<AvailabilityCheckResult> => {
    const response = await api.post<AvailabilityCheckResult>('/api/availability/check', {
      date,
      startTime,
      endTime,
      partySize,
      tableId,
    });
    return response.data;
  },

  // Get available time slots
  getSlots: async (date: string, partySize: number): Promise<TimeSlot[]> => {
    const response = await api.get<TimeSlot[]>('/api/availability/slots', {
      params: { date, partySize },
    });
    return response.data;
  },

  // Suggest tables
  suggestTables: async (
    date: string,
    startTime: string,
    endTime: string,
    partySize: number
  ): Promise<TableSuggestion[]> => {
    const response = await api.get<TableSuggestion[]>('/api/availability/suggest-tables', {
      params: { date, startTime, endTime, partySize },
    });
    return response.data;
  },

  // Check specific table
  checkTable: async (
    tableId: number,
    date: string,
    startTime: string,
    endTime: string
  ): Promise<{ tableId: number; date: string; available: boolean }> => {
    const response = await api.get<{ tableId: number; date: string; available: boolean }>(
      `/api/availability/table/${tableId}`,
      {
        params: { date, startTime, endTime },
      }
    );
    return response.data;
  },

  // Get available tables
  getTables: async (
    date: string,
    startTime: string,
    endTime: string,
    partySize?: number
  ): Promise<ReservableTable[]> => {
    const response = await api.get<ReservableTable[]>('/api/availability/tables', {
      params: { date, startTime, endTime, partySize },
    });
    return response.data;
  },
};

// ============================================================================
// SETTINGS
// ============================================================================

export const reservationSettingsApi = {
  // Get settings
  get: async (): Promise<ReservationSettings> => {
    const response = await api.get<ReservationSettings>('/api/reservation-settings');
    return response.data;
  },

  // Update settings
  update: async (data: Partial<ReservationSettings>): Promise<ReservationSettings> => {
    const response = await api.put<ReservationSettings>('/api/reservation-settings', data);
    return response.data;
  },

  // Get feature flags
  getFeatures: async (): Promise<{
    reservationsEnabled: boolean;
    waitlistEnabled: boolean;
    tableMergeEnabled: boolean;
  }> => {
    const response = await api.get<{
      reservationsEnabled: boolean;
      waitlistEnabled: boolean;
      tableMergeEnabled: boolean;
    }>('/api/reservation-settings/features');
    return response.data;
  },
};

// Default export for convenience
export default {
  reservations: reservationApi,
  tableGroups: tableGroupApi,
  waitlist: waitlistApi,
  tableCompatibility: tableCompatibilityApi,
  availability: availabilityApi,
  settings: reservationSettingsApi,
};
